// Reports what was actually on screen, as message ids.
//
// The record this feeds is one someone is accountable for, so a lost ack is
// not a cosmetic glitch: the message would read as "no evidence" forever,
// because on the reader's next visit it is no longer on screen to re-report.
// Hence the queue outlives the process — it is written to device storage on
// every change and flushed on the next launch.
//
// Ids are collected and sent in batches: a reader scrolling through a
// conversation generates one frame every DEBOUNCE_MS rather than one per row.

const DEBOUNCE_MS = 2_000;
const MAX_IDS_PER_FRAME = 200; // Matches the server's per-frame cap; a longer queue is sent as several frames

/**
 * Where the pending queue survives a restart: `localStorage` on the web,
 * `AsyncStorage` on mobile. Both are supplied by the app — shared code knows
 * neither. Failures are swallowed by the caller: a device that cannot store
 * the queue still reports what it can while it is running.
 */
export interface IReadQueueStore {
  load(): Promise<string | null>;
  save(value: string): Promise<void>;
}

/** Message ids waiting to be reported, by channel. */
type PendingByChannel = Record<string, string[]>;

export interface IReadReporterOptions {
  send: (channelId: string, messageIds: string[]) => boolean; // Sends one frame; false when the socket is not open, which keeps the ids queued.
  store: IReadQueueStore;
  debounceMs?: number; // Test seam; real callers leave it
}

export class ReadReporter {
  private pending: PendingByChannel = {};
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly debounceMs: number;
  // Set by `dispose()`: the owner is gone, and a successor may already own
  // the same store. Nothing is sent or written after this, and a `resume()`
  // that lands late queues nothing.
  private disposed = false;

  constructor(private readonly options: IReadReporterOptions) {
    this.debounceMs = options.debounceMs ?? DEBOUNCE_MS;
  }

  /** Pick up whatever a previous run could not deliver. Call once, at startup. */
  async resume(): Promise<void> {
    const raw = await this.options.store.load().catch(() => null);
    if (!raw || this.disposed) return;
    let saved: PendingByChannel;
    try {
      saved = JSON.parse(raw) as PendingByChannel;
    } catch {
      return; // unreadable queue: nothing to resume, nothing to repair
    }
    for (const [channelId, ids] of Object.entries(saved)) {
      if (Array.isArray(ids) && ids.length > 0) this.enqueue(channelId, ids);
    }
  }

  /** These messages were on screen. Safe to call with ids already reported. */
  report(channelId: string, messageIds: string[]): void {
    if (messageIds.length === 0) return;
    this.enqueue(channelId, messageIds);
  }

  /** Send everything queued now — on a socket reopening, or a screen closing. */
  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.disposed) return;
    let changed = false;
    for (const [channelId, ids] of Object.entries(this.pending)) {
      const remaining: string[] = [];
      for (let i = 0; i < ids.length; i += MAX_IDS_PER_FRAME) {
        const batch = ids.slice(i, i + MAX_IDS_PER_FRAME);
        // A closed socket keeps the ids: they are re-sent on the next flush,
        // and the server drops any the reader has already reported.
        if (!this.options.send(channelId, batch)) remaining.push(...batch);
      }
      if (remaining.length > 0) this.pending[channelId] = remaining;
      else delete this.pending[channelId];
      if (remaining.length < ids.length) changed = true;
    }
    // Only a queue that moved is written back: a flush that sent nothing must
    // not overwrite what a successor on the same store may have saved since.
    if (changed) void this.persist();
  }

  /** The owner is unmounting: stop the timer and ignore anything that lands late. */
  dispose(): void {
    this.disposed = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private enqueue(channelId: string, messageIds: string[]): void {
    if (this.disposed) return;
    const queued = new Set(this.pending[channelId] ?? []);
    for (const id of messageIds) queued.add(id);
    this.pending[channelId] = [...queued];
    void this.persist();
    if (!this.timer) this.timer = setTimeout(() => this.flush(), this.debounceMs);
  }

  private async persist(): Promise<void> {
    await this.options.store.save(JSON.stringify(this.pending)).catch(() => {});
  }
}
